import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Building2, Plus, Trash2, Link2, Edit } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const OperatorManager = ({ operators, onAddOperator, onRemoveOperator, onUpdateOperator, flags }) => {
  const { toast } = useToast();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [operatorName, setOperatorName] = useState('');
  const [currentOperator, setCurrentOperator] = useState(null);
  const [selectedFlags, setSelectedFlags] = useState([]);

  const handleAddSubmit = (e) => {
    e.preventDefault();
    
    if (!operatorName.trim()) {
      toast({ title: "Erro", description: "Por favor, digite o nome da operadora.", variant: "destructive" });
      return;
    }

    if (operators.some(op => op.name.toLowerCase() === operatorName.toLowerCase())) {
      toast({ title: "Erro", description: "Esta operadora já está cadastrada.", variant: "destructive" });
      return;
    }

    onAddOperator({
      id: Date.now(),
      name: operatorName.trim(),
      flagIds: []
    });

    toast({ title: "Sucesso!", description: "Operadora cadastrada com sucesso!" });
    setOperatorName('');
    setIsAddOpen(false);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!currentOperator) return;

    onUpdateOperator(currentOperator.id, { ...currentOperator, flagIds: selectedFlags });
    toast({ title: "Sucesso!", description: "Operadora atualizada com sucesso!" });
    setIsEditOpen(false);
    setCurrentOperator(null);
    setSelectedFlags([]);
  };

  const handleRemove = (operatorId) => {
    onRemoveOperator(operatorId);
    toast({ title: "Operadora removida", description: "A operadora foi removida com sucesso." });
  };

  const openEditModal = (operator) => {
    setCurrentOperator(operator);
    setSelectedFlags(operator.flagIds || []);
    setIsEditOpen(true);
  };

  return (
    <Card className="glass-effect border-white/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <Building2 className="w-5 h-5" />
          Gerenciar Operadoras
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Operadora
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5" />Nova Operadora</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="operatorName">Nome da Operadora</Label>
                <Input id="operatorName" placeholder="Ex: Cielo, Stone..." value={operatorName} onChange={(e) => setOperatorName(e.target.value)} required />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsAddOpen(false)}>Cancelar</Button>
                <Button type="submit" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">Adicionar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <div className="space-y-2 max-h-60 overflow-y-auto">
          {operators.length === 0 ? (
            <p className="text-white/70 text-center py-4">Nenhuma operadora cadastrada.</p>
          ) : (
            operators
              .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
              .map((op, index) => (
              <div key={op.id} className="flex items-center justify-between p-3 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
                <span className="text-white font-medium">{op.name}</span>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEditModal(op)} className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"><Link2 className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleRemove(op.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/20"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))
          )}
        </div>

        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5" />Vincular Bandeiras a {currentOperator?.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Bandeiras</Label>
                <p className="text-sm text-muted-foreground">Selecione as bandeiras aceitas por esta operadora.</p>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-md">
                  {flags.map(flag => (
                    <div key={flag.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`flag-${flag.id}`}
                        checked={selectedFlags.includes(flag.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedFlags(prev => [...prev, flag.id]);
                          } else {
                            setSelectedFlags(prev => prev.filter(id => id !== flag.id));
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <Label htmlFor={`flag-${flag.id}`}>{flag.name}</Label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancelar</Button>
                <Button type="submit" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">Salvar Vínculos</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default OperatorManager;